import { getFormattingLocale } from "@/i18n/i18n";

export function formatIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

/**
 * Format a Date as YYYY-MM-DD using UTC calendar parts.
 * Use for values returned by Android Material3 date pickers (UTC day millis).
 */
export function formatIsoDateUtc(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

/**
 * Local calendar day as UTC midnight.
 * Needed for Android `@expo/ui` DatePicker: Material3 interprets
 * `initialSelectedDateMillis` in UTC, while min/max are converted from local
 * via `toUtcDayMillis`. Passing local midnight shifts the selected day back.
 */
export function toAndroidPickerDate(date: Date): Date {
    return new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
}

/**
 * Normalize API date values to YYYY-MM-DD for calendar-day comparisons.
 * MySQL DATE columns often arrive as ISO datetimes shifted by timezone
 * (e.g. local midnight → previous UTC evening), so we convert via local date parts.
 */
export function toDateOnlyIso(value: string | Date): string {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }

    // MySQL DATETIME/DATE string without timezone: use the calendar prefix as stored.
    if (
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value) &&
        !value.endsWith("Z") &&
        !/[+-]\d{2}:\d{2}$/.test(value)
    ) {
        return value.slice(0, 10);
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return typeof value === "string" ? value.slice(0, 10) : "";
    }

    return formatIsoDate(date);
}

const TIME_MINUTES_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

/** Normalize clock values to `HH:mm`. Empty or invalid input becomes null. */
export function serializeTimeMinutes(
    value: string | null | undefined,
): string | null {
    if (value == null) {
        return null;
    }

    const trimmed = value.trim();

    if (trimmed === "") {
        return null;
    }

    const withSeconds = trimmed.length >= 8 ? trimmed.slice(0, 8) : trimmed;

    if (!TIME_MINUTES_PATTERN.test(withSeconds)) {
        return null;
    }

    return withSeconds.slice(0, 5);
}

/**
 * Live `HH:mm` mask for typed/pasted clock values.
 * Keeps digits only, clamps hours to 00–23 and minutes to 00–59, inserts `:`.
 * A first digit 3–9 is treated as a single-digit hour (`9` → `09`).
 */
export function maskTimeInput(raw: string): string {
    let digits = raw.replace(/\D/g, "").slice(0, 4);

    if (digits.length === 0) {
        return "";
    }

    if (digits[0] >= "3") {
        digits = `0${digits}`.slice(0, 4);
    }

    const hourTens = digits[0] > "2" ? "2" : digits[0];

    if (digits.length === 1) {
        return hourTens;
    }

    const hourOnes = hourTens === "2" && digits[1] > "3" ? "3" : digits[1];
    const hours = `${hourTens}${hourOnes}`;

    if (digits.length === 2) {
        return hours;
    }

    const minuteTens = digits[2] > "5" ? "5" : digits[2];

    if (digits.length === 3) {
        return `${hours}:${minuteTens}`;
    }

    return `${hours}:${minuteTens}${digits[3]}`;
}

export function formatTimeMinutes(date: Date): string {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
}

export function parseTimeMinutes(
    value: string | null | undefined,
): Date | undefined {
    const serialized = serializeTimeMinutes(value);

    if (serialized == null) {
        return undefined;
    }

    const [hours, minutes] = serialized.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    return date;
}

/** Whether this locale displays a 24-hour clock (`cs-CZ`, `en-GB`) vs 12-hour (`en-US`). */
export function uses24HourClock(
    locale: string = getFormattingLocale(),
): boolean {
    const formatted = new Date(2024, 0, 1, 23, 0, 0).toLocaleTimeString(
        locale,
        { hour: "numeric" },
    );

    return formatted.includes("23");
}

function normalizeTimeText(raw: string): string {
    return raw
        .normalize("NFKC")
        .replace(/[\u202f\u00a0]/g, " ")
        .trim();
}

/**
 * Parse a typed clock string to `HH:mm`.
 * Accepts 24h (`14:04`, `1404`) and 12h (`2:04 PM`, `2pm`) input.
 */
export function parseDisplayTimeMinutes(value: string): string | null {
    const normalized = normalizeTimeText(value);

    if (normalized === "") {
        return null;
    }

    const meridiemMatch = normalized.match(/([ap](?:\.?\s*m\.?)?)$/i);
    const clockPart = meridiemMatch
        ? normalized.slice(0, meridiemMatch.index).trim()
        : normalized;
    const meridiemToken = meridiemMatch?.[1]
        ?.replace(/[.\s]/g, "")
        .toLowerCase();
    const isPm = meridiemToken == null ? null : meridiemToken.startsWith("p");
    const clockMatch = clockPart.match(/^(\d{1,2})(?:[:.](\d{1,2}))?$/);

    if (clockMatch) {
        let hours = Number(clockMatch[1]);
        const hasMinutes = clockMatch[2] != null;
        const minutes = hasMinutes ? Number(clockMatch[2]) : 0;

        if (minutes > 59) {
            return null;
        }

        if (isPm != null) {
            if (hours < 1 || hours > 12) {
                return null;
            }

            if (hours === 12) {
                hours = isPm ? 12 : 0;
            } else if (isPm) {
                hours += 12;
            }
        } else if (hours > 23 || !hasMinutes) {
            return null;
        }

        return serializeTimeMinutes(
            `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
        );
    }

    if (isPm == null) {
        return serializeTimeMinutes(maskTimeInput(clockPart));
    }

    return null;
}

/** Locale clock string for a stored `HH:mm` value (`en-US` 12h, `cs-CZ` 24h). */
export function formatDisplayTimeMinutes(value: string): string {
    const date = parseTimeMinutes(value);

    if (!date) {
        return serializeTimeMinutes(value) ?? value;
    }

    const locale = getFormattingLocale();

    return date.toLocaleTimeString(locale, {
        hour: "numeric",
        minute: "2-digit",
        hour12: !uses24HourClock(locale),
    });
}

export function parseIsoDate(value: string): Date | undefined {
    if (!value) {
        return undefined;
    }

    const dateOnly = toDateOnlyIso(value);

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
        return undefined;
    }

    const [year, month, day] = dateOnly.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return undefined;
    }

    return date;
}

/** Shift a YYYY-MM-DD calendar date by a number of days (negative goes backward). */
export function addCalendarDays(isoDate: string, days: number): string {
    const date = parseIsoDate(isoDate);

    if (!date) {
        return isoDate;
    }

    date.setDate(date.getDate() + days);

    return formatIsoDate(date);
}

const SCHOOL_WEEKDAYS = [1, 2, 3, 4, 5];

export type FormatLeaveRequestDateRangeTimes = {
    timeFrom?: string | null;
    timeTo?: string | null;
    fromTimeLabel?: (time: string) => string;
    untilTimeLabel?: (time: string) => string;
    leavesAtLabel?: (time: string) => string;
    arrivesAtLabel?: (time: string) => string;
    weeklyLabel?: (days: string) => string;
    dailyLabel?: string;
    daysOfWeek?: number[] | null;
    weekdayLabel?: (day: number) => string;
};

function formatLeaveRequestWeekdays(
    daysOfWeek: number[] | null | undefined,
    weekdayLabel?: (day: number) => string,
): string {
    if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
        return "";
    }

    const label = weekdayLabel ?? ((day: number) => String(day));
    return [...new Set(daysOfWeek)]
        .filter((day) => day >= 1 && day <= 7)
        .sort((a, b) => a - b)
        .map((day) => label(day))
        .join(", ");
}

function isEverySchoolWeekday(daysOfWeek?: number[] | null): boolean {
    if (!Array.isArray(daysOfWeek)) {
        return false;
    }

    const unique = [...new Set(daysOfWeek)]
        .filter((day) => day >= 1 && day <= 7)
        .sort((a, b) => a - b);
    return (
        unique.length === SCHOOL_WEEKDAYS.length &&
        unique.every((day, index) => day === SCHOOL_WEEKDAYS[index])
    );
}

function coveredDayPattern(
    timeFrom: string | null,
    timeTo: string | null,
    leavesAt: (time: string) => string,
    arrivesAt: (time: string) => string,
): string | null {
    if (timeFrom && timeTo) {
        return `${timeFrom}–${timeTo}`;
    }

    if (timeFrom) {
        return leavesAt(timeFrom);
    }

    if (timeTo) {
        return arrivesAt(timeTo);
    }

    return null;
}

export function formatLeaveRequestDateRange(
    dateFrom: string,
    dateTo: string | null | undefined,
    ongoingLabel: string,
    times?: FormatLeaveRequestDateRangeTimes,
): string {
    const timeFromRaw = serializeTimeMinutes(times?.timeFrom);
    const timeToRaw = serializeTimeMinutes(times?.timeTo);
    const timeFrom = timeFromRaw ? formatDisplayTimeMinutes(timeFromRaw) : null;
    const timeTo = timeToRaw ? formatDisplayTimeMinutes(timeToRaw) : null;
    const leavesAtLabel =
        times?.leavesAtLabel ?? ((time) => `Leaves at ${time}`);
    const arrivesAtLabel =
        times?.arrivesAtLabel ?? ((time) => `Arrives at ${time}`);
    const weekdays = formatLeaveRequestWeekdays(
        times?.daysOfWeek,
        times?.weekdayLabel,
    );
    const validity =
        dateTo == null || dateTo === ""
            ? `${formatDisplayDate(dateFrom)} – ${ongoingLabel}`
            : dateFrom === dateTo
              ? formatDisplayDate(dateFrom)
              : `${formatDisplayDate(dateFrom)} – ${formatDisplayDate(dateTo)}`;
    const cadence = weekdays
        ? isEverySchoolWeekday(times?.daysOfWeek)
            ? (times?.dailyLabel ?? "Daily")
            : (times?.weeklyLabel?.(weekdays) ?? `Weekly (${weekdays})`)
        : null;

    return [
        coveredDayPattern(timeFrom, timeTo, leavesAtLabel, arrivesAtLabel),
        cadence,
        validity,
    ]
        .filter(Boolean)
        .join("\n");
}

export function formatDisplayDate(value: string): string {
    const date = parseIsoDate(value);

    if (!date) {
        return toDateOnlyIso(value) || value;
    }

    return date.toLocaleDateString(getFormattingLocale(), {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

export function formatDisplayDateTime(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return formatDisplayDate(value.slice(0, 10));
    }

    return date.toLocaleString(getFormattingLocale(), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

export function formatDisplayDateWithWeekday(value: string): string {
    const date = parseIsoDate(value);

    if (!date) {
        return toDateOnlyIso(value) || value;
    }

    return date.toLocaleDateString(getFormattingLocale(), {
        weekday: "long",
        month: "short",
        day: "numeric",
    });
}
